/**
 * Product Kit 页面：按参考站中间工作台复刻前台 UI，不接入真实上传和生成接口。
 */

import type { ChangeEvent, ReactElement } from 'react'
import { useRef, useState } from 'react'
import {
  ArrowRight,
  Check,
  CircleHelp,
  ImageUp,
  Minus,
  Plus,
  Sparkles,
  WandSparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

interface OptionItem {
  value: string
  label: string
}

interface ProductImageItem {
  id: string
  name: string
}

type StructureMode = 'auto' | 'custom'
type CustomConfigKey = 'white' | 'scene' | 'selling' | 'other'

interface CustomConfigItem {
  key: CustomConfigKey
  title: string
  description: string
  smart?: boolean
}

const PLATFORM_OPTIONS: OptionItem[] = [
  { value: 'amazon', label: '亚马逊' },
  { value: 'taobao', label: '淘宝' },
  { value: 'tiktok-shop', label: 'TikTok Shop' },
  { value: 'shopify', label: '独立站' },
]

const COUNTRY_OPTIONS: OptionItem[] = [
  { value: 'us', label: '美国' },
  { value: 'uk', label: '英国' },
  { value: 'de', label: '德国' },
  { value: 'jp', label: '日本' },
]

const LANGUAGE_OPTIONS: OptionItem[] = [
  { value: 'en-US', label: '英文' },
  { value: 'zh-CN', label: '中文' },
  { value: 'de-DE', label: '德文' },
  { value: 'ja-JP', label: '日文' },
]

const RATIO_OPTIONS: OptionItem[] = [
  { value: '1:1', label: '1:1' },
  { value: '4:5', label: '4:5' },
  { value: '16:9', label: '16:9' },
  { value: '3:4', label: '3:4' },
]

const CUSTOM_CONFIG_ITEMS: CustomConfigItem[] = [
  { key: 'white', title: '白底图', description: '白底主图，多角度呈现商品细节' },
  { key: 'scene', title: '场景图', description: '展示商品的生活使用场景和人物搭配' },
  { key: 'selling', title: '卖点图', description: '展示商品的核心卖点及细节特写' },
  { key: 'other', title: '其他', description: '对比图、尺寸图等，根据商品智能匹配', smart: true },
]

const DEFAULT_CUSTOM_COUNTS: Record<CustomConfigKey, number> = {
  white: 1,
  scene: 2,
  selling: 2,
  other: 2,
}

const DEFAULT_BRIEF = [
  '建议包含以下信息生成更精准：',
  '1. 产品名称',
  '2. 核心卖点',
  '3. 适用人群',
  '4. 期望场景',
  '5. 具体参数',
].join('\n')

const MAX_PRODUCT_IMAGES = 3
const MAX_CUSTOM_IMAGE_COUNT = 20

function FieldLabel({
  title,
  help = false,
  action,
}: {
  title: string
  help?: boolean
  action?: ReactElement
}): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-1.5 text-[15px] font-semibold leading-5 text-[#1f2329]">
        <span>{title}</span>
        {help ? <CircleHelp className="size-4 text-[#9aa3b2]" /> : null}
      </div>
      {action}
    </div>
  )
}

function SelectField({
  value,
  options,
  onValueChange,
}: {
  value: string
  options: OptionItem[]
  onValueChange: (nextValue: string) => void
}): ReactElement {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="h-[36px] cursor-pointer rounded-[10px] border-none bg-[#f3f5f9] px-4 text-[14px] font-medium text-[#242934] shadow-none focus:ring-0">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} className="cursor-pointer">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function StructureOption({
  mode,
  selected,
  title,
  description,
  onSelect,
  children,
}: {
  mode: StructureMode
  selected: boolean
  title: string
  description: string
  onSelect: (nextMode: StructureMode) => void
  children?: ReactElement
}): ReactElement {
  return (
    <div
      className={cn(
        'rounded-[12px] border bg-white transition',
        selected
          ? 'border-[#5e8cff] bg-[linear-gradient(180deg,#f7faff_0%,#ffffff_100%)] shadow-[0_8px_22px_rgba(79,121,255,0.12)]'
          : 'border-[#eef1f6] hover:border-[#dbe3f1]'
      )}
    >
      <button
        type="button"
        onClick={(): void => { onSelect(mode) }}
        className="flex w-full cursor-pointer items-start justify-between gap-4 px-4 py-3 text-left"
      >
        <span className="order-first min-w-0">
          <span className="block text-[14px] font-semibold leading-5 text-[#202633]">{title}</span>
          <span className="mt-1 block text-[12px] leading-5 text-[#7d8796]">{description}</span>
        </span>
        <span
          className={cn(
            'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-[8px] border',
            selected ? 'border-[#5e8cff] bg-[#5e8cff] text-white' : 'border-[#dce2ec] text-transparent'
          )}
        >
          <Check className="size-3.5" />
        </span>
      </button>
      {selected && children !== undefined ? <div className="px-4 pb-4">{children}</div> : null}
    </div>
  )
}

function CountButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  children: ReactElement
}): ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-8 items-center justify-center rounded-[8px] text-[#202633] transition hover:bg-[#eef2f7]',
        disabled === true ? 'cursor-not-allowed opacity-35' : 'cursor-pointer'
      )}
    >
      {children}
    </button>
  )
}

function CustomConfigRow({
  item,
  value,
  canIncrement,
  onIncrement,
  onDecrement,
}: {
  item: CustomConfigItem
  value: number
  canIncrement: boolean
  onIncrement: (key: CustomConfigKey) => void
  onDecrement: (key: CustomConfigKey) => void
}): ReactElement {
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-[10px] bg-[#f3f5f8] px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-[14px] font-semibold leading-5 text-[#202633]">{item.title}</h3>
          {item.smart === true ? (
            <span className="rounded-[5px] bg-[#e9f1ff] px-1.5 py-0.5 text-[11px] font-semibold leading-4 text-[#4f7dff]">
              AI智能匹配
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-[12px] leading-5 text-[#8a94a5]">{item.description}</p>
      </div>

      <div className="flex h-9 items-center rounded-[9px] bg-white px-1 text-[14px] font-semibold text-[#202633]">
        <CountButton
          label={`减少${item.title}数量`}
          disabled={value <= 0}
          onClick={(): void => { onDecrement(item.key) }}
        >
          <Minus className="size-4" />
        </CountButton>
        <span className="w-6 text-center tabular-nums">{value}</span>
        <CountButton
          label={`增加${item.title}数量`}
          disabled={!canIncrement}
          onClick={(): void => { onIncrement(item.key) }}
        >
          <Plus className="size-4" />
        </CountButton>
      </div>
    </div>
  )
}

export function ProductKitPage(): ReactElement {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const [platform, setPlatform] = useState('amazon')
  const [country, setCountry] = useState('us')
  const [language, setLanguage] = useState('en-US')
  const [ratio, setRatio] = useState('1:1')
  const [brief, setBrief] = useState(DEFAULT_BRIEF)
  const [productImages, setProductImages] = useState<ProductImageItem[]>([])
  const [structureMode, setStructureMode] = useState<StructureMode>('auto')
  const [customCounts, setCustomCounts] =
    useState<Record<CustomConfigKey, number>>(DEFAULT_CUSTOM_COUNTS)
  const customTotal = Object.values(customCounts).reduce((total, count) => total + count, 0)
  const targetImageCount = structureMode === 'custom' ? customTotal : 7
  const canGenerate = productImages.length > 0 && targetImageCount >= 7

  function openImagePicker(): void {
    inputRef.current?.click()
  }

  function handleImageChange(event: ChangeEvent<HTMLInputElement>): void {
    const nextImages = Array.from(event.target.files ?? [])
      .slice(0, MAX_PRODUCT_IMAGES)
      .map((file, index) => ({
        id: `${file.name}-${String(file.lastModified)}-${String(index)}`,
        name: file.name,
      }))

    setProductImages(nextImages)
    event.target.value = ''
  }

  function updateCustomCount(key: CustomConfigKey, delta: number): void {
    setCustomCounts((currentCounts) => {
      const currentTotal = Object.values(currentCounts).reduce((total, count) => total + count, 0)
      if (delta > 0 && currentTotal >= MAX_CUSTOM_IMAGE_COUNT) {
        return currentCounts
      }

      return {
        ...currentCounts,
        [key]: Math.max(0, currentCounts[key] + delta),
      }
    })
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] overflow-x-auto bg-[#f5f7fb]">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] min-w-[1180px]">
        <aside className="w-[400px] shrink-0 overflow-y-auto border-r border-[#e7ebf2] bg-white px-5 pb-6 pt-6">
          <div className="flex flex-col gap-5">
            <section className="flex flex-col gap-4">
              <FieldLabel title="商品原图" help />

              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageChange}
              />

              <button
                type="button"
                onClick={openImagePicker}
                className="grid h-[104px] w-full cursor-pointer place-items-center rounded-[14px] border border-dashed border-[#dfe5ef] bg-[#fcfdff] text-center transition-colors hover:border-[#c8d2e2]"
              >
                <div className="flex w-full flex-col items-center justify-center">
                  <div className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#f3f5f9] px-4 py-2.5 text-[14px] font-semibold text-[#242934]">
                    <ImageUp className="size-[18px]" />
                    上传图片
                  </div>
                  <p className="mt-4 max-w-[300px] truncate text-center text-[14px] text-[#8d96a6]">
                    {productImages.length > 0
                      ? `已选择 ${String(productImages.length)} 张：${productImages.map((item) => item.name).join('、')}`
                      : '同一产品，最多3张。'}
                  </p>
                </div>
              </button>
            </section>

            <section className="flex flex-col gap-4">
              <FieldLabel title="生成设置" />
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  value={platform}
                  options={PLATFORM_OPTIONS}
                  onValueChange={setPlatform}
                />
                <SelectField
                  value={country}
                  options={COUNTRY_OPTIONS}
                  onValueChange={setCountry}
                />
                <SelectField
                  value={language}
                  options={LANGUAGE_OPTIONS}
                  onValueChange={setLanguage}
                />
                <SelectField
                  value={ratio}
                  options={RATIO_OPTIONS}
                  onValueChange={setRatio}
                />
              </div>
            </section>

            <section className="flex flex-col gap-4">
              <FieldLabel
                title="商品卖点&要求"
                help
                action={(
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-[8px] bg-[#edf4ff] px-3 py-2 text-[12px] font-semibold text-[#5d86ff] transition hover:bg-[#e2edff]"
                  >
                    <WandSparkles className="size-3.5" />
                    AI 帮写
                  </button>
                )}
              />
              <Textarea
                value={brief}
                onChange={(event): void => { setBrief(event.target.value) }}
                className="h-[158px] resize-none rounded-[12px] border-[#dfe5ef] bg-white px-4 py-3 text-[13px] leading-[22px] text-[#596579] shadow-[0_2px_8px_rgba(15,23,42,0.04)] focus-visible:ring-0"
              />
            </section>

            <section className="flex flex-col gap-4">
              <FieldLabel title="套图结构配置" />
              <div className="flex flex-col gap-3">
                <StructureOption
                  mode="auto"
                  selected={structureMode === 'auto'}
                  title="智能匹配"
                  description="AI智能分析商品图，匹配最佳Listing套图"
                  onSelect={setStructureMode}
                />
                <StructureOption
                  mode="custom"
                  selected={structureMode === 'custom'}
                  title="自定义配置"
                  description="可自由调整各类型图片数量，至少选择7张"
                  onSelect={setStructureMode}
                >
                  <div className="flex flex-col gap-3">
                    {CUSTOM_CONFIG_ITEMS.map((item) => (
                      <CustomConfigRow
                        key={item.key}
                        item={item}
                        value={customCounts[item.key]}
                        canIncrement={customTotal < MAX_CUSTOM_IMAGE_COUNT}
                        onIncrement={(key): void => { updateCustomCount(key, 1) }}
                        onDecrement={(key): void => { updateCustomCount(key, -1) }}
                      />
                    ))}
                  </div>
                </StructureOption>
              </div>

              <button
                type="button"
                disabled
                className="flex h-[44px] w-full cursor-not-allowed items-center justify-center gap-2 rounded-[10px] bg-[#f3f5f9] text-[13px] font-semibold text-[#9aa3b2]"
              >
                <Sparkles className="size-4" />
                爆款风格分析
              </button>

              <Button
                disabled={!canGenerate}
                className={cn(
                  'h-[48px] w-full rounded-[10px] text-[15px] font-semibold text-white',
                  !canGenerate
                    ? 'cursor-not-allowed bg-[#c5d9ff] hover:bg-[#c5d9ff]'
                    : 'cursor-pointer bg-[#111827] hover:bg-[#111827]/90'
                )}
              >
                一键生成爆款套图（{targetImageCount}张）
                <ArrowRight className="size-4" />
              </Button>
            </section>
          </div>
        </aside>

        <main className="flex-1 bg-[#f5f7fb] px-10">
          <div className="flex min-h-[calc(100vh-4rem)] items-start justify-center pt-10">
            <p className="max-w-[520px] text-center text-[15px] leading-7 text-[#7b8494]">
              上传商品原图并填写左侧配置后，生成结果会在这里展示。
            </p>
          </div>
        </main>
      </div>
    </div>
  )
}
